/**
 * 계정 줄에 글을 붙이는 규칙 — 계정별 표 · 글 예산 나누기 · 글 예산 접기가 이 한 벌로 글을 계정에 붙인다.
 * 셋이 다르게 붙이면 「글 예산 합 = 계정별 표 예산」이 깨진다.
 */

/**
 * 계정 이름 맞추기 — 앞뒤 공백 · 앞 `@` 하나를 떼고 소문자로. 등록 계정과 글쓴 계정 양쪽에 같이 쓴다.
 * @returns 비면 빈 문자열
 */
export function normalizeCampaignAccountName(value: unknown): string {
  return String(value ?? '').trim().replace(/^@/, '').trim().toLowerCase();
}

/**
 * 계정 줄의 열쇠 — 객체 줄은 채널이 있으면 소문자 `채널*계정`, 없으면 계정 이름. 문자열 줄은 `채널*계정` 이면 그 둘, 아니면 계정 이름.
 * 계정 이름은 `normalizeCampaignAccountName`(대소문자 · 앞 `@` 무시).
 * @returns 계정 이름이 비면 빈 문자열
 */
export function campaignAccountRowKey(acc: unknown): string {
  if (!acc) return '';
  if (typeof acc === 'object') {
    const row = acc as { platform?: unknown; platformId?: unknown; account?: unknown; username?: unknown };
    return rowKey(row.platform || row.platformId, row.account || row.username);
  }
  const str = String(acc || '').trim();
  const star = str.indexOf('*');
  return star >= 0 ? rowKey(str.slice(0, star), str.slice(star + 1)) : rowKey('', str);
}

/** 글쓴 계정의 열쇠 — 채널이 있으면 소문자 `채널*계정`, 없으면 계정 이름(`normalizeCampaignAccountName`) */
export function campaignPostAccountRowKey(post: any): string {
  return rowKey(post?.source || post?.platform, post?.account || post?.username);
}

/**
 * 계정 줄마다 그 계정의 글.
 * - `채널*계정` 줄은 같은 채널·계정의 글만.
 * - 이름만 적힌 줄은 채널 없는 같은 이름의 글, 그런 글이 하나도 없으면 채널을 가리지 않고 같은 이름의 글.
 * @param keys 계정 줄 열쇠(`campaignAccountRowKey`)
 * @returns `keys` 와 같은 길이·차례 — 줄마다 받은 차례대로의 글
 */
export function groupCampaignPostsByAccountKeys<T>(keys: readonly string[], posts: readonly T[]): T[][] {
  const byKey = new Map<string, T[]>();
  (Array.isArray(posts) ? posts : []).forEach((post) => {
    const key = campaignPostAccountRowKey(post);
    if (!key) return;
    const list = byKey.get(key);
    if (list) list.push(post);
    else byKey.set(key, [post]);
  });
  return keys.map((key) => {
    if (!key) return [];
    const exact = byKey.get(key) ?? [];
    if (exact.length > 0 || key.includes('*')) return [...exact];
    const collected: T[] = [];
    byKey.forEach((list, k) => {
      const parts = k.split('*');
      if (parts.length >= 2 && parts[1] === key) collected.push(...list);
    });
    return collected;
  });
}

/** 계정 줄(객체·문자열 섞임)마다 그 계정의 글 — `groupCampaignPostsByAccountKeys` 를 줄 모양으로 */
export function groupCampaignPostsByAccountRows<T>(accounts: readonly unknown[] | null | undefined, posts: readonly T[]): T[][] {
  return groupCampaignPostsByAccountKeys((Array.isArray(accounts) ? accounts : []).map(campaignAccountRowKey), posts);
}

function rowKey(platform: unknown, account: unknown): string {
  const plat = String(platform ?? '').trim().toLowerCase();
  const a = normalizeCampaignAccountName(account);
  return plat && a ? `${plat}*${a}` : a;
}
